import type { ReactNode } from 'react'
import { Skeleton } from '@/components/ui/skeleton'
import { displayMapper } from '@/lib/display-mapper'
import type { DashboardSummaryDto, StatsResponse } from './types'

/**
 * Operational pulse — a compact strip of leak-site KPIs describing the global
 * ransomware landscape (independent of the workbench feed filters).
 *
 * Every metric is backed by a real endpoint: claim volumes from
 * `/leak-sites/stats` (24h / 7d / 30d / all tracked), and active-group / top-
 * group / top-sector / top-region from `/leak-sites/dashboard-summary`
 * (trailing 7 days, preference-aware). The design's unbacked metrics — mirror
 * health, new-groups-30d, week-over-week deltas — are deliberately omitted
 * rather than invented.
 *
 * Mirrors the dashboard `OperationalPulseStrip` look: a gap-px divider grid of
 * <li> items, each with one label + one value (no nested interactive content).
 */

const fmt = (n: number) => n.toLocaleString('en-GB')

function PulseItem(props: {
  label: string
  isLoading: boolean
  value: ReactNode
  aside?: ReactNode
}) {
  return (
    <li className="min-w-0 bg-card p-3">
      <span className="block truncate text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
        {props.label}
      </span>
      {props.isLoading ? (
        <Skeleton className="mt-1.5 h-6 w-14" />
      ) : (
        <span className="mt-0.5 flex flex-wrap items-baseline gap-1.5">
          <span className="font-heading text-lg font-semibold leading-tight">
            {props.value}
          </span>
          {props.aside}
        </span>
      )}
    </li>
  )
}

const DASH = <span className="text-muted-foreground">—</span>

export interface LeakSitesPulseStripProps {
  stats?: StatsResponse
  summary?: DashboardSummaryDto
  isLoading: boolean
}

export function LeakSitesPulseStrip({
  stats,
  summary,
  isLoading,
}: LeakSitesPulseStripProps) {
  const t = stats?.totals
  const topGroup = summary?.topGroup
  const topSector = summary?.topSector
  const topCountry = summary?.topCountry

  return (
    <ul
      aria-label="Operational pulse"
      className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-border bg-border sm:grid-cols-4 xl:grid-cols-8"
    >
      <PulseItem
        label="Claims 24h"
        isLoading={isLoading}
        value={t ? fmt(t.last24h) : DASH}
      />
      <PulseItem
        label="Claims 7d"
        isLoading={isLoading}
        value={t ? fmt(t.last7d) : DASH}
      />
      <PulseItem
        label="Claims 30d"
        isLoading={isLoading}
        value={t ? fmt(t.last30d) : DASH}
      />
      <PulseItem
        label="Total tracked"
        isLoading={isLoading}
        value={t ? fmt(t.total) : DASH}
      />
      <PulseItem
        label="Active groups 7d"
        isLoading={isLoading}
        value={summary ? fmt(summary.uniqueGroups) : DASH}
      />
      <PulseItem
        label="Top group 7d"
        isLoading={isLoading}
        value={
          topGroup ? (
            <span className="font-mono text-base">{topGroup.name}</span>
          ) : (
            DASH
          )
        }
        aside={
          topGroup ? (
            <span className="whitespace-nowrap text-xs text-muted-foreground">
              {fmt(topGroup.count)}
            </span>
          ) : null
        }
      />
      <PulseItem
        label="Top sector 7d"
        isLoading={isLoading}
        value={
          topSector ? (
            <span className="text-base">
              {displayMapper.sector(topSector.sector)}
            </span>
          ) : (
            DASH
          )
        }
        aside={
          topSector ? (
            <span className="whitespace-nowrap text-xs text-muted-foreground">
              {fmt(topSector.count)}
            </span>
          ) : null
        }
      />
      <PulseItem
        label="Top region 7d"
        isLoading={isLoading}
        value={
          topCountry ? (
            <span className="text-base">
              {displayMapper.country(topCountry.country)}
            </span>
          ) : (
            DASH
          )
        }
        aside={
          topCountry ? (
            <span className="whitespace-nowrap text-xs text-muted-foreground">
              {fmt(topCountry.count)}
            </span>
          ) : null
        }
      />
    </ul>
  )
}
